import React, { memo, useMemo, useState, useCallback, useEffect, useContext } from 'react';
import { H3 } from 'tea-component';
import { SortBy } from "tea-component/table/addons";
import TriggerViewCard from '@gp/components/TriggerViewCard';
import DataTime from '@gp/components/DataTime';
// import SvgIcon from '@gp/components/Iconfont';
import MergeableTable from './components/MergeableTable';
import TableForPDFDownload from './components/MergeableTable/TableForPDFDownload';
import LineEchartCon from './components/LineEchartCon';
import BarEchartCon from './components/BarEchartCon';
import { context as Context } from '@gp/components/Context';
import { isEqual } from 'lodash-es';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import { useRequest, useMemoizedFn } from 'ahooks';
import {
  download5YHeadcountsOfPeople,
  queryFiveYearHeadcountsList,
  queryAll5YHeadcountsOfPeople,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import { getStudioModuleName } from '../../../../../utils';
import { FILE_TYPES1, FILE_TYPES_MAP } from '@gp/constants/const';
import useHeadcountsList from '../../hook/useHeadcountsList';
import useDownloadTablePDF from '../../hook/useDownloadTablePDF';
import './index.scss';

import SvgIcon from '@/pages/home/components/SvgIcon';
import CardBtn from '@/pages/globalpedia/components/CardBtn';

interface HeadcountsPropsType {}

// 5Y Headcounts模块名称
const MODULE_NAME = '5Y Headcounts';

// 5Y Headcounts模块 PDF下载 需要用到的className
const PDF_DOWNLOAD_NODE_CLASSNAME =
  'dbg-studio-overall-details-people-5y-headcounts-pdf-card';

const DEFAULT_PROPS: HeadcountsPropsType = {};

function Headcounts(props: HeadcountsPropsType) {
  const {} = props;

  const [filter, setFilter] = useState<FilterType>({
    // 类型: 1:function 2:projects
    type: '1',
    start_date: '',
    end_date: '',
  });
  // 之前筛选器请求过的数据，用于function/projects反复切换时减少接口请求
  const [lastFilterHistoryDataList, setLastFilterHistoryDataListDataList] =
    useState<orgHeadcountsLastFilterHistoryDataTypes>([]);
  const [dataList, setDataList] = useState<orgHeadcountsDataTypes>(null);
  const [sorts, setSorts] = useState<SortBy>({
    by: '',
    order: undefined,
  });

  const { studioId, studioName }: StudioOverallDetailContextTypes = useContext(Context) || {};

  const {
    data: responseRes,
    loading,
    run,
  } = useRequest(queryFiveYearHeadcountsList, {manual: true});

  // 处理接口数据
  const {
    orgData,
  } = useHeadcountsList({
    loading,
    responseRes,
    module: '5y_headcounts',
  });

  const {
    isAllDatasResLoading,
    allDatas,
    updateAllDatas,
  } = useDownloadTablePDF(
    queryAll5YHeadcountsOfPeople,
    '5y_headcounts',
  );

  const { isDownloading, setIsDownloading, handleDownload } = useDownload({
    fileName: getStudioModuleName(MODULE_NAME, studioName),
    csvDownloadLogKey: 'STUDIO_OVERALL_DETAIL_PEOPLE_5Y_HEADCOUNTS_CSV_SUBMIT',
    pdfDownloadLogKey: 'STUDIO_OVERALL_DETAIL_PEOPLE_5Y_HEADCOUNTS_PDF_SUBMIT',
    node: document.querySelector(`.${PDF_DOWNLOAD_NODE_CLASSNAME}`),
    downloadService: createDownloadService(download5YHeadcountsOfPeople),
    nodeOptions: {
      ownerStyle: 'visibility: visible;',
    },
  });

  // function/projects切换发生变化
  useEffect(() => {
    if (!['1', '2'].includes(filter?.type)) return;
    // 对比是否拥有历史数据
    const hadDataList =
      lastFilterHistoryDataList?.find((t) => {
        const had = t?.filterSign?.type === filter?.type;
        return had;
      })?.dataList || undefined;
    // 当之前有请求过筛选器条件数据时，用之前的
    if (hadDataList) {
      setDataList(hadDataList);
      return;
    }
    // 请求接口
    run({
      studio_id: studioId,
      type: Number(filter?.type) as (1 | 2), // 1: function 2: projects
      order_metric: sorts?.by,
      order: sorts?.order,
    });
  }, [filter?.type]);

  // 接口返回数据
  useEffect(() => {
    if (loading) return;
    // 接口请求失败
    if (!orgData?.isSuccessReq) {
      setDataList(null);
      return;
    }
    setLastFilterHistoryDataListDataList([
      ...lastFilterHistoryDataList,
      {
        filterSign: filter,
        dataList: orgData,
      },
    ]);
    setDataList(orgData);
  }, [orgData, loading]);

  const handleChange = useCallback(
    (type: HeadcountsDataType) => {
      const newFilter = {
        ...filter,
        type: type || '1',
      };
      setFilter(newFilter);
    },
    [filter],
  );

  const handleSortsChange = useMemoizedFn((sorts: SortBy) => {
    setSorts(sorts);
    run({
      studio_id: studioId,
      type: Number(filter?.type) as (1 | 2), // 1: function 2: projects
      order_metric: sorts?.by,
      order: sorts?.order,
    });
  });

  const tabs = useMemo(() => {
    return [
      {
        id: 'chart-bar',
        label: <CardBtn onClick={() => {}} type="column-chart" />,
        component: <BarEchartCon />,
      },
      {
        id: 'chart-line',
        label: <CardBtn onClick={() => {}} type="discount-chart" />,
        component: <LineEchartCon />,
      },
      {
        id: 'table',
        label: <CardBtn onClick={() => {}} type="sheet" />,
        component: (
          <MergeableTable
            sorts={sorts}
            onSortsChange={handleSortsChange}
          />
        ),
      },
      {
        id: 'download',
        label: (
          <GeneralDownloadBtn
            uiType="2"
            fileTypes={FILE_TYPES1}
            isLoading={isDownloading}
            onConfirm={async (payload) => {
              if (isDownloading) return;
              const params = {
                ...payload,
                file_type: Number.parseInt(payload?.fileType),
                people_headcounts: {
                  studio_id: studioId,
                  type: Number.parseInt(filter?.type),
                  order_metric: sorts?.by,
                  order: sorts?.order,
                },
              };
              if (payload?.fileType === FILE_TYPES_MAP.PDF) {
                setIsDownloading?.(true);
                await updateAllDatas?.(params);
                setIsDownloading?.(false);
              }
              handleDownload?.(params);
            }}
          />
        ),
        component: null,
      },
    ];
  }, [studioId, filter?.type, isDownloading, setIsDownloading, handleDownload, sorts]);

  const pdfTabs = useMemo(() => {
    return [
      {
        id: 'table',
        label: null,
        component: <TableForPDFDownload />,
      },
    ];
  }, []);

  const updateTime = useMemo(() => {
    if (loading) return '';
    return (responseRes as any)?.data?.data?.update_time;
  }, [loading]);

  const titleContent = useMemo(() => {
    return (
      <div className="dbg-studio-overall-people-flex">
        {MODULE_NAME}
        {!!updateTime && (
          <DataTime
            className="dbg-common-data-time dbg-studio-overall-key-information-time dbg-common-data-time-left"
            dateType="Month"
            date={updateTime}
          />
        )}
      </div>
    );
  }, [updateTime]);

  return (
    <Context.Provider
      value={{
        loading,
        dataList,
        isAllDatasResLoading,
        allDatas,
        updateTime,
        filter,
        sorts,
        onChange: handleChange,
      }}
    >
      <div className="dbg-studio-overall-details-people-5y-headcounts-card-container">
        <TriggerViewCard
          className="dbg-studio-overall-details-people-5y-headcounts-content"
          title={titleContent}
          loading={false}
          isNoData={false}
          tabs={tabs}
          defaultActiveId="chart-bar"
        />
        <TriggerViewCard
          className="dbg-studio-overall-details-people-5y-headcounts-content"
          // 这个rootNodeClassName 是为了 下载PDF用的 不要删除
          rootNodeClassName={PDF_DOWNLOAD_NODE_CLASSNAME}
          title={titleContent}
          loading={false}
          isNoData={false}
          tabs={pdfTabs}
          defaultActiveId="table"
        />
      </div>
    </Context.Provider>
  );
}

Headcounts.defaultProps = DEFAULT_PROPS;

export default memo(Headcounts, (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
});
